import { useState, type FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ApiException, authApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') || '';
    const [pwd, setPwd] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-900">
                <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 text-center">
                    <div className="text-4xl mb-4" aria-hidden="true">⚠️</div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Link inválido</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                        Este link no tiene un token de recuperación válido.
                    </p>
                    <Link
                        to="/forgot-password"
                        className="inline-block px-4 py-2 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-blue-dark"
                    >
                        Solicitar nuevo link
                    </Link>
                </div>
            </div>
        );
    }

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setError(null);
        if (pwd.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres.');
            return;
        }
        if (pwd !== confirm) {
            setError('Las contraseñas no coinciden.');
            return;
        }
        setSubmitting(true);
        try {
            await authApi.resetPassword(token, pwd);
            setDone(true);
            setTimeout(() => navigate('/login', { replace: true }), 2500);
        } catch (err) {
            if (err instanceof ApiException) {
                setError(err.body.error || 'No se pudo restablecer la contraseña.');
            } else {
                setError((err as Error).message);
            }
        } finally {
            setSubmitting(false);
        }
    }

    if (done) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-900">
                <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 text-center">
                    <div className="text-4xl mb-4" aria-hidden="true">✅</div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Contraseña actualizada</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Te estamos redirigiendo al login...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-900">
            <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Nueva contraseña</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    Elegí una contraseña nueva para tu cuenta.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Input
                        label="Nueva contraseña"
                        type="password"
                        required
                        autoFocus
                        minLength={6}
                        value={pwd}
                        onChange={e => setPwd(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                    />
                    <Input
                        label="Confirmar contraseña"
                        type="password"
                        required
                        minLength={6}
                        value={confirm}
                        onChange={e => setConfirm(e.target.value)}
                    />
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    <Button
                        type="submit"
                        loading={submitting}
                        disabled={!pwd || !confirm}
                        className="w-full"
                    >
                        Restablecer contraseña
                    </Button>
                </form>
            </div>
        </div>
    );
}
